import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  Newspaper,
  Megaphone,
  Wrench,
  Image as ImageIcon,
  Check,
  FolderOpen,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import AdminMediaLibrary from './AdminMediaLibrary';
import { toRelativeMediaPath } from '../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const PAGE_TYPES = [
  { value: 'service_page', label: 'Tjenesteinfo', icon: Wrench, color: 'blue' },
  { value: 'news', label: 'Nyheter', icon: Newspaper, color: 'green' },
  { value: 'campaign', label: 'Kampanje', icon: Megaphone, color: 'purple' },
  { value: 'general', label: 'Generell', icon: FileText, color: 'gray' }
];

const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMediaSelectOpen, setIsMediaSelectOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    page_type: 'general',
    content: '',
    excerpt: '',
    featured_image: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    meta_title_override: false,
    service_id: '',
    published: false
  });
  const [saving, setSaving] = useState(false);
  
  const { toast } = useToast();
  const { token } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pagesRes, servicesRes] = await Promise.all([
        axios.get(`${API}/pages`),
        axios.get(`${API}/services`)
      ]);
      
      setPages(pagesRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke laste data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (page = null) => {
    if (page) {
      // Fetch full page data using ID endpoint (works for both published and unpublished)
      try {
        const response = await axios.get(`${API}/pages/${page.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const fullPage = response.data;
        
        setEditingPage(fullPage);
        setFormData({
          title: fullPage.title,
          slug: fullPage.slug,
          page_type: fullPage.page_type,
          content: fullPage.content || '',
          excerpt: fullPage.excerpt || '',
          featured_image: fullPage.featured_image || '',
          meta_title: fullPage.meta_title || fullPage.title,
          meta_description: fullPage.meta_description || '',
          meta_keywords: fullPage.meta_keywords || '',
          meta_title_override: fullPage.meta_title_override || false,
          service_id: fullPage.service_id || '',
          published: fullPage.published,
          published_date: fullPage.published_date ? new Date(fullPage.published_date).toISOString().split('T')[0] : (fullPage.created_at ? new Date(fullPage.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
        });
      } catch (error) {
        console.error('Error fetching page content:', error);
        toast({
          title: "Feil",
          description: "Kunne ikke laste sideinnhold",
          variant: "destructive"
        });
        return;
      }
    } else {
      setEditingPage(null);
      setFormData({
        title: '',
        slug: '',
        page_type: 'general',
        content: '',
        excerpt: '',
        featured_image: '',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        meta_title_override: false,
        service_id: '',
        published: false,
        published_date: new Date().toISOString().split('T')[0]
      });
    }
    setIsModalOpen(true);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[æÆ]/g, 'ae')
      .replace(/[øØ]/g, 'o')
      .replace(/[åÅ]/g, 'a')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (newTitle) => {
    const updates = { title: newTitle };
    if (!editingPage || formData.slug === generateSlug(formData.title)) {
      updates.slug = generateSlug(newTitle);
    }
    setFormData({ ...formData, ...updates });
  };

  const getImagePreviewUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const getPageUrl = (page) => {
    const typePrefix = {
      service_page: '/tjeneste',
      news: '/aktuelt',
      campaign: '/kampanje',
      general: ''
    };
    const prefix = typePrefix[page.page_type] || '';
    return prefix ? `${prefix}/${page.slug}` : `/${page.slug}`;
  };

  const handleSave = async () => {
    if (!formData.title) {
      toast({
        title: "Feil",
        description: "Tittel er påkrevd",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      
      // Auto-generate meta_title if empty
      const dataToSave = {
        ...formData,
        meta_title: formData.meta_title || formData.title
      };

      if (editingPage) {
        await axios.put(`${API}/pages/${editingPage.id}`, dataToSave, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Oppdatert! ✅",
          description: "Side oppdatert"
        });
      } else {
        await axios.post(`${API}/pages`, dataToSave, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Opprettet! ✅",
          description: "Ny side opprettet"
        });
      }
      
      setIsModalOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error saving page:', error);
      toast({
        title: "Feil",
        description: error.response?.data?.detail || "Kunne ikke lagre side",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne siden?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Slettet! ✅",
        description: "Side fjernet"
      });
      
      await fetchData();
    } catch (error) {
      console.error('Error deleting page:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke slette side",
        variant: "destructive"
      });
    }
  };

  const handleTogglePublish = async (page) => {
    try {
      await axios.put(`${API}/pages/${page.id}`, 
        { published: !page.published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Oppdatert! ✅",
        description: page.published ? "Side skjult" : "Side publisert"
      });
      
      await fetchData();
    } catch (error) {
      console.error('Error toggling publish:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke oppdatere status",
        variant: "destructive"
      });
    }
  };

  const handleSelectMedia = (media) => {
    // Use original format URL via backend endpoint (reliable in all environments)
    const preferred = media.versions?.medium || media.versions?.large;
    const imageUrl = toRelativeMediaPath(preferred?.url || media.url);
    setFormData({ ...formData, featured_image: imageUrl });
    setIsMediaSelectOpen(false);
    toast({
      title: "Bilde valgt!",
      description: `Valgte: ${media.title || media.original_filename || 'Featured image'}`
    });
  };

  const getPageTypeInfo = (type) => {
    return PAGE_TYPES.find(pt => pt.value === type) || PAGE_TYPES[3];
  };

  const filteredPages = filterType === 'all' 
    ? pages 
    : pages.filter(p => p.page_type === filterType);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">Laster sider...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
              Side-CMS
            </span>
          </h2>
          <p className="text-gray-400">Administrer tjeneste-infosider, nyheter og kampanjer</p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ny side
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex space-x-2 border-b border-gray-700">
        <button
          onClick={() => setFilterType('all')}
          className={`px-4 py-2 font-semibold transition-colors ${
            filterType === 'all'
              ? 'text-white border-b-2 border-red-600'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Alle ({pages.length})
        </button>
        {PAGE_TYPES.map(type => {
          const Icon = type.icon;
          const count = pages.filter(p => p.page_type === type.value).length;
          return (
            <button
              key={type.value}
              onClick={() => setFilterType(type.value)}
              className={`px-4 py-2 font-semibold transition-colors flex items-center space-x-2 ${
                filterType === type.value
                  ? 'text-white border-b-2 border-red-600'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{type.label} ({count})</span>
            </button>
          );
        })}
      </div>

      {/* Pages list */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardContent className="p-6">
          {filteredPages.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Ingen sider ennå</p>
              <p className="text-sm mt-2">Klikk "Ny side" for å komme i gang</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPages.map((page) => {
                const typeInfo = getPageTypeInfo(page.page_type);
                const TypeIcon = typeInfo.icon;
                
                return (
                  <div
                    key={page.id}
                    className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        {page.featured_image && typeof page.featured_image === 'string' && (
                          <img
                            src={getImagePreviewUrl(page.featured_image)}
                            alt={page.title}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-white font-semibold text-lg">{page.title}</h3>
                            <Badge variant="outline" className={`text-${typeInfo.color}-400 border-${typeInfo.color}-600`}>
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {typeInfo.label}
                            </Badge>
                            {page.published ? (
                              <Badge className="bg-green-600/20 text-green-400 border-green-600/50">
                                <Eye className="w-3 h-3 mr-1" />
                                Publisert
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-600/20 text-gray-400 border-gray-600/50">
                                <EyeOff className="w-3 h-3 mr-1" />
                                Utkast
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-400 mb-2">
                            {getPageUrl(page)}
                            {page.published && (
                              <a
                                href={getPageUrl(page)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 inline-flex items-center text-blue-400 hover:text-blue-300"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </p>
                          {page.excerpt && (
                            <p className="text-sm text-gray-500">{page.excerpt}</p>
                          )}
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span>{page.views || 0} visninger</span>
                            <span>Opprettet: {new Date(page.created_at).toLocaleDateString('no-NO')}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => handleTogglePublish(page)}
                          variant="outline"
                          size="sm"
                          className="border-gray-600 text-gray-300"
                        >
                          {page.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          onClick={() => handleOpenModal(page)}
                          variant="outline"
                          size="sm"
                          className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(page.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-600 text-red-400 hover:bg-red-600/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPage ? 'Rediger side' : 'Opprett ny side'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingPage ? 'Oppdater sideinnholdet' : 'Opprett en ny side for tjeneste, nyhet eller kampanje'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 mb-2 block">Tittel *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Side tittel..."
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300 mb-2 block">Slug (URL)</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="auto-genereres fra tittel"
                  className="bg-gray-800 border-gray-700 text-white"
                />
                {formData.slug && (
                  <p className="text-xs text-gray-500 mt-1">
                    Offentlig URL: {formData.page_type === 'service_page' ? `/tjeneste/${formData.slug}` : formData.page_type === 'news' ? `/aktuelt/${formData.slug}` : formData.page_type === 'campaign' ? `/kampanje/${formData.slug}` : `/${formData.slug}`}
                  </p>
                )}
              </div>
            </div>

            {/* Page Type */}
            <div>
              <Label className="text-gray-300 mb-2 block">Side-type</Label>
              <div className="grid grid-cols-4 gap-2">
                {PAGE_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setFormData({ ...formData, page_type: type.value })}
                      className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center space-y-2 ${
                        formData.page_type === type.value
                          ? 'border-red-600 bg-red-600/20'
                          : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="text-sm">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Service Link (for service_page type) */}
            {formData.page_type === 'service_page' && (
              <div>
                <Label className="text-gray-300 mb-2 block">Koble til tjeneste</Label>
                <select
                  value={formData.service_id}
                  onChange={(e) => setFormData({ ...formData, service_id: e.target.value })}
                  className="w-full bg-gray-800 border-gray-700 text-white rounded-lg p-2"
                >
                  <option value="">Ingen tjeneste</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>{service.title}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Published Date */}
            <div>
              <Label className="text-gray-300 mb-2 block">Publiseringsdato</Label>
              <Input
                type="date"
                value={formData.published_date}
                onChange={(e) => setFormData({ ...formData, published_date: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white w-48"
                data-testid="published-date-input"
              />
              <p className="text-xs text-gray-500 mt-1">Dato som vises på den offentlige siden</p>
            </div>

            {/* Excerpt */}
            <div>
              <Label className="text-gray-300 mb-2 block">Kort beskrivelse (excerpt)</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Kort sammendrag som vises i lister..."
                className="bg-gray-800 border-gray-700 text-white min-h-[60px]"
              />
            </div>

            {/* Content */}
            <div>
              <Label className="text-gray-300 mb-2 block">Innhold (HTML/Markdown)</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Skriv sideinnholdet her... Du kan bruke HTML-tags."
                className="bg-gray-800 border-gray-700 text-white min-h-[200px] font-mono text-sm"
              />
            </div>

            {/* Featured Image */}
            <div>
              <Label className="text-gray-300 mb-2 block">Featured Image</Label>
              <div className="space-y-3">
                {/* Image Preview */}
                {formData.featured_image && typeof formData.featured_image === 'string' && formData.featured_image.length > 0 && (
                  <div className="relative w-full h-40 bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
                    <img
                      src={getImagePreviewUrl(formData.featured_image)}
                      alt="Featured"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {/* URL Input with Media Picker Button */}
                <div className="flex gap-2">
                  <Input
                    value={formData.featured_image}
                    onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                    placeholder="Bilde-URL eller velg fra mediebibliotek"
                    className="flex-1 bg-gray-800 border-gray-700 text-white"
                  />
                  <Button
                    type="button"
                    onClick={() => setIsMediaSelectOpen(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="open-media-picker-btn"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Velg bilde
                  </Button>
                  {formData.featured_image && (
                    <Button
                      type="button"
                      onClick={() => setFormData({ ...formData, featured_image: '' })}
                      variant="outline"
                      className="border-red-600 text-red-400 hover:bg-red-600/20"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Lim inn en URL direkte eller velg et bilde fra mediebiblioteket
                </p>
              </div>
            </div>

            {/* SEO Fields */}
            <div className="border-t border-gray-700 pt-4">
              <h3 className="text-white font-semibold mb-3">SEO Innstillinger</h3>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-gray-300 mb-2 block">Meta Tittel</Label>
                  <Input
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    placeholder="Bruker sidetittel hvis tom"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                
                <div className="flex items-center space-x-2 py-2">
                  <input
                    type="checkbox"
                    id="meta_title_override"
                    checked={formData.meta_title_override}
                    onChange={(e) => setFormData({ ...formData, meta_title_override: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-700 bg-gray-900"
                  />
                  <Label htmlFor="meta_title_override" className="text-gray-300 cursor-pointer">
                    Bruk kun meta-tittel (ikke legg til "| Sand-tec AS")
                  </Label>
                </div>
                
                <div>
                  <Label className="text-gray-300 mb-2 block">Meta Beskrivelse</Label>
                  <Textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="Beskrivelse for søkemotorer (150-160 tegn)"
                    className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.meta_description.length} tegn</p>
                </div>
                
                <div>
                  <Label className="text-gray-300 mb-2 block">Nøkkelord (kommaseparert)</Label>
                  <Input
                    value={formData.meta_keywords}
                    onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                    placeholder="eu-kontroll, tungbil, service"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Publish Status */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="w-4 h-4 rounded border-gray-700 bg-gray-900"
              />
              <Label htmlFor="published" className="text-gray-300 cursor-pointer">
                Publiser side (synlig for besøkende)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              Avbryt
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Lagrer...' : 'Lagre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Select Modal - Using AdminMediaLibrary */}
      <Dialog open={isMediaSelectOpen} onOpenChange={setIsMediaSelectOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-400" />
              <span>Velg featured image</span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Klikk på et bilde for å velge det, eller last opp et nytt
            </DialogDescription>
          </DialogHeader>

          <AdminMediaLibrary 
            pickerMode={true} 
            onSelect={handleSelectMedia}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPages;
